// components/profile/SkillsAndPreferences.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Sparkles,
  Plus,
  X,
  Globe,
  Briefcase,
  Clock,
  Edit3,
  Save,
  ChevronDown,
  Check,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import {
  ILanguage,
  ISkillsAndPreferences,
  LanguageProficiency,
  WeeklyAvailability,
} from "../../lib/types/authOnboarding";

interface SkillsAndPreferencesProps {
  data: ISkillsAndPreferences;
  onSave: (data: ISkillsAndPreferences) => Promise<boolean>;
  isPending: boolean;
  isAnotherSectionEditing?: boolean;
  onEditingChange?: (isEditing: boolean) => void;
}

const AVAILABLE_SKILLS = [
  "Machine Learning",
  "Natural Language Processing",
  "Data Annotation",
  "Python",
  "Critical Thinking",
  "Content Moderation",
  "Image Classification",
  "Text Analysis",
  "Data Labeling",
  "Quality Assurance",
  "Research",
  "Translation",
  "Sentiment Analysis",
  "Speech Recognition",
  "Computer Vision",
];

const TASK_CATEGORIES = [
  { id: "ai_training", label: "AI Training" },
  { id: "data_annotation", label: "Data Annotation" },
  { id: "content_moderation", label: "Content Moderation" },
  { id: "code_review", label: "Code Review" },
  { id: "research", label: "Research" },
  { id: "translation", label: "Translation" },
];

const AVAILABILITY_OPTIONS: WeeklyAvailability[] = [
  "Less than 10 hours",
  "10-20 hours",
  "20-30 hours",
  "30-40 hours",
  "40+ hours",
];

const PROFICIENCY_LEVELS: LanguageProficiency[] = [
  "Native",
  "Fluent",
  "Intermediate",
  "Basic",
];

const SkillsAndPreferences: React.FC<SkillsAndPreferencesProps> = ({
  data,
  onSave,
  isPending,
  isAnotherSectionEditing = false,
  onEditingChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ISkillsAndPreferences>(data);
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [newLanguage, setNewLanguage] = useState<ILanguage>({
    name: "",
    proficiency: "Intermediate",
  });
  const [skillSearch, setSkillSearch] = useState("");

  // Reset form when data changes
  useEffect(() => {
    if (!isEditing) {
      setFormData(data);
    }
  }, [data, isEditing]);

  // Notify parent about editing state changes
  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  // Check if form has changes
  const hasChanges = useMemo(() => {
    return (
      JSON.stringify(formData.skills) !== JSON.stringify(data.skills) ||
      JSON.stringify(formData.languages) !== JSON.stringify(data.languages) ||
      JSON.stringify(formData.preferredCategories) !==
        JSON.stringify(data.preferredCategories) ||
      formData.weeklyAvailability !== data.weeklyAvailability
    );
  }, [formData, data]);

  // Check if profile is incomplete
  const isProfileIncomplete = useMemo(() => {
    return (
      data.skills.length === 0 ||
      data.languages.length === 0 ||
      data.preferredCategories.length === 0 ||
      !data.weeklyAvailability
    );
  }, [data]);

  // Get completion stats
  const completionStats = useMemo(() => {
    const items = [
      { label: "Skills", complete: formData.skills.length > 0 },
      { label: "Languages", complete: formData.languages.length > 0 },
      {
        label: "Categories",
        complete: formData.preferredCategories.length > 0,
      },
      { label: "Availability", complete: !!formData.weeklyAvailability },
    ];
    const completed = items.filter((i) => i.complete).length;
    const missing = items.filter((i) => !i.complete).map((i) => i.label);
    return { completed, total: items.length, missing };
  }, [formData]);

  const handleSave = async () => {
    if (isPending) return;
    const success = await onSave(formData);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setFormData(data);
    setIsEditing(false);
    setShowSkillDropdown(false);
  };

  const handleStartEditing = () => {
    if (isAnotherSectionEditing) return;
    setIsEditing(true);
  };

  const addSkill = (skill: string) => {
    if (!formData.skills.includes(skill)) {
      setFormData((prev) => ({
        ...prev,
        skills: [...prev.skills, skill],
      }));
    }
    setShowSkillDropdown(false);
    setSkillSearch("");
  };

  const removeSkill = (skill: string) => {
    setFormData((prev) => ({
      ...prev,
      skills: prev.skills.filter((s) => s !== skill),
    }));
  };

  const addLanguage = () => {
    if (newLanguage.name.trim()) {
      const newLang: ILanguage = {
        name: newLanguage.name.trim(),
        proficiency: newLanguage.proficiency,
      };
      setFormData((prev) => ({
        ...prev,
        languages: [...prev.languages, newLang],
      }));
      setNewLanguage({ name: "", proficiency: "Intermediate" });
      setShowLanguageModal(false);
    }
  };

  const removeLanguage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index),
    }));
  };

  const updateLanguageProficiency = (
    index: number,
    proficiency: LanguageProficiency
  ) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.map((l, i) =>
        i === index ? { ...l, proficiency } : l
      ),
    }));
  };

  const toggleCategory = (categoryId: string) => {
    setFormData((prev) => ({
      ...prev,
      preferredCategories: prev.preferredCategories.includes(categoryId)
        ? prev.preferredCategories.filter((c) => c !== categoryId)
        : [...prev.preferredCategories, categoryId],
    }));
  };

  const filteredSkills = AVAILABLE_SKILLS.filter(
    (skill) =>
      !formData.skills.includes(skill) &&
      skill.toLowerCase().includes(skillSearch.toLowerCase())
  );

  const getProficiencyColor = (proficiency: LanguageProficiency) => {
    switch (proficiency) {
      case "Native":
        return "bg-emerald-100 text-emerald-700";
      case "Fluent":
        return "bg-blue-100 text-blue-700";
      case "Intermediate":
        return "bg-amber-100 text-amber-700";
      case "Basic":
        return "bg-gray-100 text-gray-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <>
      {/* Add Language Modal */}
      {showLanguageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowLanguageModal(false)}
          />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Add Language</h3>
              <button
                onClick={() => setShowLanguageModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Language
                </label>
                <input
                  type="text"
                  value={newLanguage.name}
                  onChange={(e) =>
                    setNewLanguage((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  placeholder="e.g., German, Spanish, French"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Proficiency Level
                </label>
                <select
                  value={newLanguage.proficiency}
                  onChange={(e) =>
                    setNewLanguage((prev) => ({
                      ...prev,
                      proficiency: e.target.value as LanguageProficiency,
                    }))
                  }
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none transition-all bg-white"
                >
                  {PROFICIENCY_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 p-4 sm:p-6 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => setShowLanguageModal(false)}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addLanguage}
                disabled={!newLanguage.name.trim()}
                className="w-full sm:w-auto px-6 py-3 sm:py-2 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Add Language
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Component */}
      <div
        id="skills-preferences-section"
        className={`bg-white rounded-2xl shadow-sm border transition-all duration-300 ${
          isEditing
            ? "ring-2 ring-amber-500 border-amber-500"
            : "border-gray-100 hover:shadow-md"
        }`}
      >
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div
                className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition-all ${
                  isEditing
                    ? "bg-gradient-to-br from-amber-500 to-orange-500"
                    : "bg-gradient-to-br from-amber-500/10 to-orange-500/10"
                }`}
              >
                <Sparkles
                  className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    isEditing ? "text-white" : "text-amber-500"
                  }`}
                />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Skills & Preferences
                </h2>
                <p className="text-xs sm:text-sm text-gray-500">
                  Your expertise and work preferences
                </p>
              </div>
            </div>

            {/* Desktop Edit Button */}
            {!isEditing && (
              <button
                onClick={handleStartEditing}
                disabled={isAnotherSectionEditing}
                className={`hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                  isAnotherSectionEditing
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>

          {/* Editing Mode Banner */}
          {isEditing && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-amber-700">
                  Editing Mode
                </span>
              </div>
            </div>
          )}

          {/* Profile Incomplete Warning - Show when not editing */}
          {!isEditing && isProfileIncomplete && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Profile Incomplete
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Add your skills and preferences to get matched with relevant
                    tasks.
                  </p>
                </div>
                <button
                  onClick={handleStartEditing}
                  disabled={isAnotherSectionEditing}
                  className="text-sm font-medium text-amber-700 hover:text-amber-800 underline"
                >
                  Complete Now
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
          {/* Mobile Edit Button */}
          {!isEditing && (
            <button
              onClick={handleStartEditing}
              disabled={isAnotherSectionEditing}
              className={`sm:hidden w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
                isAnotherSectionEditing
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Edit3 className="w-4 h-4" />
              Edit Skills & Preferences
            </button>
          )}

          {/* Skills & Expertise Section */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-gray-400" />
                Skills & Expertise
                {formData.skills.length === 0 && !isEditing && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Required
                  </span>
                )}
              </h3>
              {isEditing && (
                <div className="relative">
                  <button
                    onClick={() => setShowSkillDropdown(!showSkillDropdown)}
                    className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:px-3 sm:py-1.5 text-sm font-medium text-amber-600 border border-amber-400 rounded-xl sm:rounded-lg hover:bg-amber-50 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add Skill
                  </button>

                  {showSkillDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowSkillDropdown(false)}
                      />
                      <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 w-full sm:w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-20 max-h-64 overflow-hidden">
                        <div className="px-3 pb-2">
                          <input
                            type="text"
                            value={skillSearch}
                            onChange={(e) => setSkillSearch(e.target.value)}
                            placeholder="Search skills..."
                            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredSkills.length > 0 ? (
                            filteredSkills.map((skill) => (
                              <button
                                key={skill}
                                onClick={() => addSkill(skill)}
                                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                {skill}
                              </button>
                            ))
                          ) : (
                            <p className="px-4 py-3 text-sm text-gray-500">
                              No skills found
                            </p>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {formData.skills.map((skill) => (
                <span
                  key={skill}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 sm:py-1.5 rounded-lg text-sm font-medium transition-all ${
                    isEditing
                      ? "bg-gray-100 text-gray-700 pr-2"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {skill}
                  {isEditing && (
                    <button
                      onClick={() => removeSkill(skill)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <X className="w-3.5 h-3.5 text-gray-500" />
                    </button>
                  )}
                </span>
              ))}
              {formData.skills.length === 0 && (
                <div
                  className={`w-full p-4 rounded-xl text-center ${
                    isEditing
                      ? "bg-gray-50 border-2 border-dashed border-gray-200"
                      : "bg-amber-50 border border-amber-200"
                  }`}
                >
                  <p
                    className={`text-sm ${
                      isEditing ? "text-gray-500" : "text-amber-600"
                    }`}
                  >
                    {isEditing
                      ? "Click 'Add Skill' to get started"
                      : "No skills added yet"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Languages Section */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Globe className="w-4 h-4 text-gray-400" />
                Languages
                {formData.languages.length === 0 && !isEditing && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Required
                  </span>
                )}
              </h3>
              {isEditing && (
                <button
                  onClick={() => setShowLanguageModal(true)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:px-3 sm:py-1.5 text-sm font-medium text-amber-600 border border-amber-400 rounded-xl sm:rounded-lg hover:bg-amber-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Language
                </button>
              )}
            </div>

            <div className="space-y-2">
              {formData.languages.map((language, index) => (
                <div
                  key={`${language.name}-${index}`}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4 bg-gray-50 rounded-xl"
                >
                  <span className="font-medium text-gray-900">
                    {language.name}
                  </span>
                  <div className="flex items-center justify-between sm:justify-end gap-2">
                    {isEditing ? (
                      <>
                        <select
                          value={language.proficiency}
                          onChange={(e) =>
                            updateLanguageProficiency(
                              index,
                              e.target.value as LanguageProficiency
                            )
                          }
                          className="flex-1 sm:flex-none px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 bg-white"
                        >
                          {PROFICIENCY_LEVELS.map((level) => (
                            <option key={level} value={level}>
                              {level}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => removeLanguage(index)}
                          className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      </>
                    ) : (
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${getProficiencyColor(
                          language.proficiency
                        )}`}
                      >
                        {language.proficiency}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {formData.languages.length === 0 && (
                <div
                  className={`w-full p-4 rounded-xl text-center ${
                    isEditing
                      ? "bg-gray-50 border-2 border-dashed border-gray-200"
                      : "bg-amber-50 border border-amber-200"
                  }`}
                >
                  <p
                    className={`text-sm ${
                      isEditing ? "text-gray-500" : "text-amber-600"
                    }`}
                  >
                    {isEditing
                      ? "Click 'Add Language' to get started"
                      : "No languages added yet"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Work Preferences Section */}
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Briefcase className="w-4 h-4 text-gray-400" />
              Work Preferences
            </h3>

            {/* Preferred Task Categories */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Preferred Task Categories
                {formData.preferredCategories.length === 0 && !isEditing && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Required
                  </span>
                )}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                {TASK_CATEGORIES.map((category) => {
                  const isSelected = formData.preferredCategories.includes(
                    category.id
                  );
                  return (
                    <button
                      key={category.id}
                      onClick={() => isEditing && toggleCategory(category.id)}
                      disabled={!isEditing}
                      className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                        isSelected
                          ? "border-amber-500 bg-amber-50"
                          : isEditing
                            ? "border-gray-200 hover:border-gray-300"
                            : "border-gray-200"
                      } ${
                        isEditing
                          ? "cursor-pointer active:scale-[0.98]"
                          : "cursor-default"
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                          isSelected
                            ? "border-amber-500 bg-amber-500"
                            : "border-gray-300"
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          isSelected ? "text-amber-700" : "text-gray-700"
                        }`}
                      >
                        {category.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {formData.preferredCategories.length === 0 && !isEditing && (
                <p className="mt-2 text-xs text-amber-600">
                  Select at least one category
                </p>
              )}
            </div>

            {/* Weekly Availability */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 text-gray-400" />
                Weekly Availability
                {!formData.weeklyAvailability && !isEditing && (
                  <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                    Required
                  </span>
                )}
              </label>
              {isEditing ? (
                <div className="relative">
                  <select
                    value={formData.weeklyAvailability}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        weeklyAvailability: e.target
                          .value as WeeklyAvailability,
                      }))
                    }
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 focus:outline-none transition-all bg-white appearance-none"
                  >
                    <option value="">Select availability...</option>
                    {AVAILABILITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              ) : (
                <div
                  className={`px-4 py-3 rounded-xl ${
                    formData.weeklyAvailability
                      ? "bg-gray-50 text-gray-700"
                      : "bg-amber-50 border border-amber-200 text-amber-600"
                  }`}
                >
                  {formData.weeklyAvailability || "Not set"}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons (Attached to Component) */}
          {isEditing && (
            <div className="pt-6 border-t border-gray-100">
              {/* Status Indicator */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {hasChanges ? (
                    <>
                      <div className="w-2 h-2 bg-amber-500 rounded-full" />
                      <span className="text-sm text-amber-600 font-medium">
                        Unsaved changes
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-green-600">
                        No changes made
                      </span>
                    </>
                  )}
                </div>

                <div className="text-xs text-gray-500">
                  {completionStats.completed}/{completionStats.total} sections
                  complete
                </div>
              </div>

              {/* Completion Warning */}
              {completionStats.missing.length > 0 && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      Missing sections
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      {completionStats.missing.join(", ")}
                    </p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleCancel}
                  disabled={isPending}
                  className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>

                <button
                  onClick={handleSave}
                  disabled={isPending || !hasChanges}
                  className={`flex-1 px-6 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                    hasChanges && !isPending
                      ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-90 shadow-lg shadow-amber-500/25"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>

              {/* Helper Text */}
              {!hasChanges && (
                <p className="text-xs text-gray-500 text-center mt-3">
                  Make changes to enable the save button
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SkillsAndPreferences;
